/* ss-bundle-builder — Build Your Own Bundle
 * Native custom element, no framework. Cart handling matches assets/lp-spin.js.
 *
 * Category tabs and reward tiers are both section blocks, so they reorder in the
 * customizer. Each tier is a % discount, free shipping or a free gift, and unlocks on
 * item count or cart spend depending on the section's "Unlock tiers by" setting.
 *
 * A tier carries BOTH thresholds, and a threshold of 0 means "this rung does not exist in
 * that mode". That is how one set of blocks drives two different ladders: a 5% rung with
 * no spend threshold only appears when counting items, and a free-shipping rung only
 * appears when counting dollars. The Shopify Function applies the same rule, so switching
 * between the two ladders is flipping `mode` in the metafield, not rewriting the tiers.
 *
 * A tier can also carry a gift product REGARDLESS of its reward type, so "15% off and a
 * cooler at $450" is one rung rather than two.
 *
 * DISCOUNTS ARE NOT APPLIED HERE. The "Bundle Builder" Shopify Function (app
 * sunni-bundle-discount) reads the _bundle_id property below and prices the bundle
 * server-side. That is what makes the saving impossible to get without this builder —
 * discount codes could not tell how an item entered the cart. This file only has to
 * write the properties and show the shopper the same maths the function will charge.
 *
 * SALE MODE turns the whole percentage ladder off. Products are already marked down, so
 * the saving shown is the real compare-at minus price on each line and the total is just
 * the sum of the sale prices — no extra percentage on top. The only ladder left is a
 * spend threshold for a free gift. Because the function is what actually charges, sale
 * mode is NOT safe until custom.bundle_config also carries saleMode: true.
 *
 * Every line added carries _source / _bundle_id / _bundle_tier / _bundle_slot so bundle
 * composition can be reported from the Orders API.
 */
(function () {
  'use strict';

  function money(cents) {
    var v = (cents / 100).toFixed(2);
    return '$' + v.replace(/\.00$/, '');
  }
  function moneyExact(cents) { return '$' + (cents / 100).toFixed(2); }

  function bundleId() {
    var s = 'abcdefghijklmnopqrstuvwxyz0123456789', out = '';
    for (var i = 0; i < 8; i++) out += s.charAt(Math.floor(Math.random() * s.length));
    return out;
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- cart drawer helpers (same approach as assets/lp-spin.js) ---------- */

  function openCartDrawer() {
    var d = document.querySelector('#cart-drawer, cart-drawer, [data-cart-drawer]');
    if (d) {
      if (typeof d.show === 'function') { try { d.show(); return; } catch (e) {} }
      d.setAttribute('open', '');
      d.classList.add('active', 'is-open', 'animate');
      d.style.visibility = 'visible';
    } else {
      var icon = document.querySelector('[data-cart-icon], #cart-icon-bubble, .header__icon--cart, a[href="/cart"]');
      if (icon) icon.click();
    }
  }

  function syncCartCount(fresh) {
    var src = fresh.querySelector('cart-count, .count-bubble');
    if (!src) return;
    var val = (src.textContent || '').trim();
    document.querySelectorAll('cart-count, .count-bubble, [data-cart-count]').forEach(function (el) {
      if (el.closest('#cart-drawer')) return;
      el.textContent = val;
    });
  }

  function refreshDrawer() {
    return fetch('/?section_id=cart-drawer', { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var cur = document.querySelector('#cart-drawer, cart-drawer');
        if (!cur || !html) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#cart-drawer, cart-drawer');
        if (!fresh) return;
        cur.innerHTML = fresh.innerHTML;
        syncCartCount(fresh);
      })
      .catch(function () {});
  }

  /* assets/cart-gifts.js monkey-patches window.fetch and reconciles gifts ~350ms after
     any /cart/add. Redirecting straight to checkout would cut that off and the shopper
     would silently lose their gift — so when a gift is expected, wait for it to land. */
  function waitForGifts(expectGift, timeoutMs) {
    if (!expectGift || !document.getElementById('cart-gift-config')) return Promise.resolve();
    var deadline = Date.now() + (timeoutMs || 3000);
    return new Promise(function (resolve) {
      (function poll() {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var has = (cart.items || []).some(function (i) { return i.properties && i.properties._gift; });
            if (has || Date.now() > deadline) resolve();
            else setTimeout(poll, 300);
          })
          .catch(resolve);
      })();
    });
  }

  /* ---------- element ---------- */

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      var cfgEl = this.querySelector('[data-bb-config]');
      var cfg = {};
      try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { cfg = {}; }

      /* Sale mode rewrites the rules rather than adding to them: no percentage ladder,
         savings come from compare-at, and progress is always measured in spend because
         a free gift threshold in item-count makes no sense against marked-down stock. */
      this.sale = cfg.sale && cfg.sale.on ? cfg.sale : null;
      this.mode = this.sale ? 'spend' : (cfg.mode === 'spend' ? 'spend' : 'qty');
      this.ctaAction = cfg.ctaAction === 'checkout' ? 'checkout' : 'drawer';

      var self = this;
      if (this.sale) {
        /* One synthetic tier so the existing ladder, progress bar and gap message all
           work unchanged. No gift product configured means no ladder at all. */
        this.tiers = this.sale.giftVariant ? [{
          type: 'gift',
          spend: true,
          threshold: this.sale.giftSpend || 0,
          pct: 0,
          label: this.sale.giftLabel || 'FREE GIFT',
          giftVariant: this.sale.giftVariant,
          giftTitle: this.sale.giftTitle || '',
          giftImage: this.sale.giftImage || '',
          giftValue: this.sale.giftValue || 0
        }].filter(function (t) { return t.threshold > 0; }) : [];
      } else {
        this.tiers = (cfg.tiers || [])
          .map(function (t) {
            /* A free-shipping rung is a promise about a cart TOTAL — Shopify grants it on
               a spend condition — so it is measured in dollars even when the rest of the
               ladder counts items. That lets free shipping live in a quantity ladder without
               becoming a lie: the chip reads "Spend $220" and lights on the real subtotal,
               while the % rungs go on counting items. */
            var isSpend = self.mode === 'spend' || t.type === 'shipping';
            return {
              type: t.type || 'discount',
              spend: isSpend,
              threshold: isSpend ? (t.minSpend || 0) : (t.minQty || 0),
              pct: t.type === 'discount' ? (t.pct || 0) : 0,
              label: t.label || '',
              giftVariant: t.giftVariant || null,
              giftTitle: t.giftTitle || '',
              giftImage: t.giftImage || '',
              giftValue: t.giftValue || 0
            };
          })
          /* Threshold 0 means the rung does not exist in this mode. */
          .filter(function (t) { return t.threshold > 0; })
          .sort(function (a, b) {
            /* In an item-count ladder the shipping rung is measured in a different currency,
               so it cannot be ordered against item counts. It goes first — it is the cheapest
               rung to reach and reads as the entry step. */
            if (a.spend !== b.spend) return a.spend ? -1 : 1;
            return a.threshold - b.threshold;
          });
      }

      this.ctaLabel = this.dataset.cta || 'Add bundle to cart';
      this.giftKicker = this.dataset.giftKicker || 'Free gift +';
      this.sel = new Map();

      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-bb-card]'));
      this.bar = this.querySelector('[data-bb-bar]');

      this.buildLadders();
      this.bindCards();
      this.bindTabs();
      this.bindSearch();
      this.bindPeek();
      this.bindAdd();
      this.render();
    }

    /* ----- ladders (page + sticky bar share one renderer) ----- */

    buildLadders() {
      var self = this;
      if (!this.tiers.length) {
        this.querySelectorAll('[data-bb-ladder]').forEach(function (host) { host.innerHTML = ''; });
        return;
      }

      /* Only reserve the kicker line if some rung actually needs one, otherwise every
         ladder pays for vertical space nothing uses. */
      var anyKicker = this.tiers.some(function (t) {
        return t.giftVariant && t.type !== 'gift';
      });

      var steps = this.tiers.map(function (t, i) {
        /* A gift reads as a reward when you can see the actual product; a generic parcel
           icon reads as decoration. Any rung carrying a gift shows its photo, including a
           % rung that hands one over on top of the discount. */
        var art = t.giftImage
          ? '<img class="bb__step-img" src="' + esc(t.giftImage) + '" alt="" width="20" height="20" loading="lazy">'
          : '';
        /* A rung giving a discount AND a gift needs to say so — the chip only has room for
           one of the two. A gift-type rung already reads "FREE GIFT", so it gets no kicker.
           The span renders on every rung, blank ones included, so the chips all start on
           the same line and the "Spend $450" captions stay on one baseline. */
        var kicker = (t.giftVariant && t.type !== 'gift') ? self.giftKicker : '';
        return '<li class="bb__step" data-bb-step="' + i + '">' +
          (anyKicker ? '<span class="bb__step-kicker">' + esc(kicker) + '</span>' : '') +
          '<span class="bb__step-chip">' + art + esc(t.label || self.autoLabel(t)) +
            (art ? '' : self.icon(t.type)) + '</span>' +
          '<span class="bb__step-req">' + esc(self.req(t)) + '</span>' +
        '</li>';
      }).join('');

      var html =
        '<p class="bb__ladder-msg" data-bb-ladder-msg></p>' +
        '<div class="bb__ladder-track"><div class="bb__ladder-fill" data-bb-ladder-fill></div></div>' +
        '<ol class="bb__steps" style="--bb-steps:' + this.tiers.length + '">' + steps + '</ol>';

      this.querySelectorAll('[data-bb-ladder]').forEach(function (host) { host.innerHTML = html; });
    }

    autoLabel(t) {
      if (t.type === 'shipping') return 'FREE SHIPPING';
      if (t.type === 'gift') return 'FREE GIFT';
      return t.pct + '% OFF';
    }

    icon(type) {
      if (type === 'shipping') {
        return ' <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6.5h10.5v9H3z"/><path d="M13.5 9.5H17l3.5 3.5v2.5h-7z"/><circle cx="7" cy="18" r="1.7"/><circle cx="17" cy="18" r="1.7"/></svg>';
      }
      if (type === 'gift') {
        return ' <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12v9H4v-9M2 7h20v5H2zM12 21V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';
      }
      return '';
    }

    /* the requirement line under each chip — follows the rung, not the ladder */
    req(t) {
      if (t.spend) return 'Spend ' + money(t.threshold);
      return t.threshold + ' item' + (t.threshold === 1 ? '' : 's');
    }

    /* ----- selection ----- */

    bindCards() {
      var self = this;
      this.cards.forEach(function (card) {
        var toggle = card.querySelector('[data-bb-toggle]');
        var inc = card.querySelector('[data-bb-inc]');
        var dec = card.querySelector('[data-bb-dec]');
        if (toggle) toggle.addEventListener('click', function () { self.toggle(card); });
        if (inc) inc.addEventListener('click', function (e) { e.stopPropagation(); self.bump(card, 1); });
        if (dec) dec.addEventListener('click', function (e) { e.stopPropagation(); self.bump(card, -1); });
      });
    }

    toggle(card) {
      var id = card.dataset.vid;
      if (this.sel.has(id)) this.sel.delete(id);
      else {
        var d = card.dataset;
        this.sel.set(d.vid, {
          vid: d.vid, qty: 1, price: parseInt(d.price, 10) || 0,
          compare: parseInt(d.compare, 10) || 0,
          title: d.title, handle: d.handle, cat: d.cat, img: d.img
        });
      }
      this.render();
    }

    bump(card, delta) {
      var item = this.sel.get(card.dataset.vid);
      if (!item) return;
      item.qty += delta;
      if (item.qty < 1) this.sel.delete(card.dataset.vid);
      this.render();
    }

    /* ----- derived state ----- */

    count() { var n = 0; this.sel.forEach(function (i) { n += i.qty; }); return n; }
    subtotal() { var t = 0; this.sel.forEach(function (i) { t += i.price * i.qty; }); return t; }
    progress() { return this.mode === 'spend' ? this.subtotal() : this.count(); }

    /* What the bundle would have cost at full price. Falls back to the selling price on
       anything with no compare-at, so a mixed cart of sale and full-price stock still
       adds up to something honest. */
    compareTotal() {
      var t = 0;
      this.sel.forEach(function (i) {
        t += (i.compare > i.price ? i.compare : i.price) * i.qty;
      });
      return t;
    }

    saleSavings() { return this.compareTotal() - this.subtotal(); }

    /* Each rung is tested in its own currency, so a dollar-based shipping rung and an
       item-based discount rung can sit in the same ladder. */
    met(t) { return (t.spend ? this.subtotal() : this.progress()) >= t.threshold; }

    unlocked() {
      var self = this;
      return this.tiers.filter(function (t) { return self.met(t); });
    }

    nextTier() {
      for (var i = 0; i < this.tiers.length; i++) if (!this.met(this.tiers[i])) return this.tiers[i];
      return null;
    }

    activeDiscount() {
      if (this.sale) return null;   // marked-down stock is never discounted again
      var best = null;
      this.unlocked().forEach(function (t) {
        if (t.type === 'discount' && (!best || t.pct > best.pct)) best = t;
      });
      return best;
    }

    /* A bundle is at least two things in it, whatever the reward ladder happens to say.
       Gating on the first tier instead broke as soon as the ladder became spend-based:
       a first rung of free shipping at $220 refused to add a single $180 rug, and the
       "add $58 to start saving" message was wrong anyway — $220 buys free shipping, not
       a saving. In sale mode one item is enough, because the saving is already in the price. */
    minItems() { return this.sale ? 1 : 2; }

    /* the one sentence that does the shopper's maths for them, in either mode */
    gapMessage() {
      var next = this.nextTier();
      if (!next) {
        if (!this.unlocked().length) return '';
        return this.sale ? "You've unlocked your free gift." : "You've unlocked every reward.";
      }
      var gap = next.threshold - (next.spend ? this.subtotal() : this.progress());
      var what = esc(next.label || this.autoLabel(next));
      if (next.spend) {
        return 'You are <strong>' + money(gap) + '</strong> away from <strong>' + what + '</strong>';
      }
      return 'Add <strong>' + gap + ' more item' + (gap === 1 ? '' : 's') + '</strong> to unlock <strong>' + what + '</strong>';
    }

    /* ----- filtering ----- */

    bindTabs() {
      var self = this;
      var tabs = this.querySelectorAll('[data-bb-tab]');
      tabs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          tabs.forEach(function (b) {
            b.classList.toggle('is-on', b === btn);
            b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
          });
          self.activeTab = btn.dataset.bbTab;
          self.activeTabUrl = btn.dataset.bbTaburl || '';
          self.activeTabLabel = btn.textContent.trim();
          self.filter();
        });
      });
      this.activeTab = 'all';
    }

    bindSearch() {
      var self = this, input = this.querySelector('[data-bb-search]'), t;
      if (!input) return;
      input.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          self.query = (input.value || '').trim().toLowerCase();
          self.filter();
        }, 140);
      });
    }

    filter() {
      var tab = this.activeTab || 'all', q = this.query || '', shown = 0;
      this.cards.forEach(function (card) {
        var vis = (tab === 'all' || card.dataset.tab === tab) &&
                  (!q || (card.dataset.search || '').indexOf(q) !== -1);
        card.hidden = !vis;
        if (vis) shown++;
      });

      var empty = this.querySelector('[data-bb-empty]');
      if (empty) empty.hidden = shown !== 0;

      var link = this.querySelector('[data-bb-tablink]');
      if (link) {
        if (this.activeTabUrl && tab !== 'all') {
          link.innerHTML = '<a href="' + esc(this.activeTabUrl) + '">Shop all ' +
            esc(this.activeTabLabel.toLowerCase()) + ' →</a>';
          link.hidden = false;
        } else {
          link.hidden = true;
        }
      }
    }

    bindPeek() {
      var self = this, peek = this.querySelector('[data-bb-peek]');
      if (!peek) return;
      peek.addEventListener('click', function () {
        var open = self.classList.toggle('bb--sheet');
        self.bar.classList.toggle('is-open', open);
        peek.setAttribute('aria-expanded', open ? 'true' : 'false');
        self.measureBar();
      });
    }

    /* the sheet sits directly on top of the bar, so its offset has to track the real
       bar height — which changes with the number of tiers and the message wrapping */
    measureBar() {
      if (!this.bar || this.bar.hidden) return;
      var h = Math.round(this.bar.getBoundingClientRect().height);
      if (h) this.style.setProperty('--bb-bar-h', h + 'px');
    }

    /* ----- render ----- */

    render() {
      var self = this;
      var n = this.count();
      var sub = this.subtotal();
      var disc = this.activeDiscount();
      var pct = disc ? disc.pct : 0;

      /* Sale mode shows what the gear was worth against what it costs today. Normal mode
         shows the selling subtotal against the bundle percentage. Both end at the same
         Total row, so the panel reads identically either way. */
      var headline, savings, total;
      if (this.sale) {
        headline = this.compareTotal();
        savings = this.saleSavings();
        total = sub;
      } else {
        headline = sub;
        savings = Math.round(sub * pct / 100);
        total = sub - savings;
      }

      var p = this.progress();
      var unlocked = this.unlocked();
      var msgHtml = this.gapMessage();

      /* cards */
      this.cards.forEach(function (card) {
        var item = self.sel.get(card.dataset.vid);
        var qtyEl = card.querySelector('[data-bb-qty]');
        var addEl = card.querySelector('.bb__add');
        var nEl = card.querySelector('[data-bb-n]');
        card.classList.toggle('is-on', !!item);
        if (addEl) {
          addEl.hidden = !!item;
          addEl.setAttribute('aria-pressed', item ? 'true' : 'false');
        }
        if (qtyEl) qtyEl.hidden = !item;
        if (nEl && item) nEl.textContent = item.qty;
      });

      /* every ladder — page and sticky bar */
      /* The bar tracks whatever the ladder is counting, so it is scaled by the furthest
         rung in that same currency. A dollar-based shipping rung inside an item ladder
         still lights up — it just is not what the bar measures. */
      var top = 0;
      this.tiers.forEach(function (t) {
        if (t.spend === (self.mode === 'spend')) top = Math.max(top, t.threshold);
      });
      this.querySelectorAll('[data-bb-ladder]').forEach(function (host) {
        host.querySelectorAll('[data-bb-step]').forEach(function (step, i) {
          step.classList.toggle('is-on', !!self.tiers[i] && self.met(self.tiers[i]));
        });
        var fill = host.querySelector('[data-bb-ladder-fill]');
        if (fill && top) fill.style.width = Math.min(100, (p / top) * 100) + '%';
        var msg = host.querySelector('[data-bb-ladder-msg]');
        if (msg) msg.innerHTML = msgHtml;
      });

      /* selected list */
      var list = this.querySelector('[data-bb-list]');
      if (list) {
        list.innerHTML = '';
        this.sel.forEach(function (i) {
          var li = document.createElement('li');
          li.className = 'bb__li';
          var was = (self.sale && i.compare > i.price)
            ? '<s class="bb__li-was">' + moneyExact(i.compare * i.qty) + '</s> '
            : '';
          li.innerHTML =
            (i.img ? '<img src="' + esc(i.img) + '" alt="" width="42" height="42" loading="lazy">' : '<span></span>') +
            '<p class="bb__li-t">' + (i.qty > 1 ? i.qty + ' &times; ' : '') + esc(i.title) + '</p>' +
            '<span class="bb__li-p">' + was + moneyExact(i.price * i.qty) +
            ' <button type="button" class="bb__li-x" aria-label="Remove ' + esc(i.title) + '">&times;</button></span>';
          li.querySelector('.bb__li-x').addEventListener('click', function () {
            self.sel.delete(i.vid);
            self.render();
          });
          list.appendChild(li);
        });
      }

      var ph = this.querySelector('[data-bb-placeholder]');
      if (ph) ph.hidden = n > 0;

      /* rewards that are not the % itself — free shipping, and any gift the tier carries */
      var rewards = this.querySelector('[data-bb-rewards]');
      if (rewards) {
        rewards.innerHTML = unlocked.map(function (t) {
          var out = '';
          if (t.type === 'shipping') {
            out += '<p class="bb__reward"><span>Free shipping</span>' +
                   '<span class="bb__reward-free">FREE</span></p>';
          }
          /* Listed whenever the tier carries a gift, whatever its reward type — a %
             rung can hand over a cooler as well as a discount. */
          if (t.giftVariant) {
            var worth = t.giftValue > 0
              ? '<span class="bb__reward-worth">worth ' + moneyExact(t.giftValue) + '</span>' : '';
            out += '<p class="bb__reward">' +
              (t.giftImage ? '<img src="' + esc(t.giftImage) + '" alt="" width="26" height="26">' : '') +
              '<span>' + esc(t.giftTitle || 'Free gift') + worth + '</span>' +
              '<span class="bb__reward-free">FREE</span></p>';
          }
          return out;
        }).join('');
      }

      /* totals */
      this.setText('[data-bb-count]', n + ' item' + (n === 1 ? '' : 's'));
      this.setText('[data-bb-sublabel]', this.sale ? (this.sale.valueLabel || 'Full price') : 'Subtotal');
      this.setText('[data-bb-subtotal]', moneyExact(headline));
      this.setText('[data-bb-total]', moneyExact(total));
      this.setText('[data-bb-savings]', '−' + moneyExact(savings));
      this.setText('[data-bb-savelabel]', this.sale
        ? (this.sale.savingsLabel || "You're saving")
        : 'Bundle saving (' + pct + '%)');
      var saveRow = this.querySelector('[data-bb-saverow]');
      if (saveRow) saveRow.hidden = savings <= 0;

      /* sticky bar */
      if (this.bar) {
        this.bar.hidden = n === 0;
        this.classList.toggle('bb--has-bar', n > 0);
        if (n === 0) { this.classList.remove('bb--sheet'); this.bar.classList.remove('is-open'); }
        this.setText('[data-bb-barcount]', n + ' item' + (n === 1 ? '' : 's'));
        var bmsg = this.querySelector('[data-bb-barmsg]');
        if (bmsg) {
          bmsg.innerHTML = msgHtml ||
            (this.sale && savings > 0
              ? '<strong>' + moneyExact(savings) + '</strong> saved'
              : '<strong>' + moneyExact(total) + '</strong> total');
        }
        requestAnimationFrame(function () { self.measureBar(); });
      }

      /* CTA */
      var need = this.minItems() - n;
      var ready = need <= 0;
      this.querySelectorAll('[data-bb-add]').forEach(function (b) { b.disabled = !ready; });
      this.querySelectorAll('[data-bb-ctatext]').forEach(function (el) {
        if (ready) el.textContent = self.ctaLabel + ' · ' + moneyExact(total);
        else el.textContent = 'Add ' + need + ' more item' + (need === 1 ? '' : 's');
      });
    }

    setText(sel, txt) {
      this.querySelectorAll(sel).forEach(function (el) { el.textContent = txt; });
    }

    /* ----- add to cart ----- */

    bindAdd() {
      var self = this;
      this.querySelectorAll('[data-bb-add]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.addBundle(); });
      });
    }

    addBundle() {
      var self = this;
      if (this.busy) return;
      var n = this.count();
      var p = this.progress();
      if (n < this.minItems()) return;

      this.busy = true;
      var labels = this.querySelectorAll('[data-bb-ctatext]');
      var prev = [];
      labels.forEach(function (el) { prev.push(el.textContent); el.textContent = 'Adding…'; });
      this.querySelectorAll('[data-bb-add]').forEach(function (b) { b.disabled = true; });

      var disc = this.activeDiscount();
      var unlocked = this.unlocked();
      var bid = bundleId();
      /* Prefixed so a report can separate sale-period bundles from percentage ones —
         the two are not comparable and averaging them together would be meaningless. */
      var tierKey = this.sale
        ? 'sale-' + n + '-items'
        : (this.mode === 'spend' ? moneyExact(p) : n + '-items') + '-' + (disc ? disc.pct : 0);

      var items = [];
      var slot = 0;
      this.sel.forEach(function (i) {
        slot++;
        items.push({
          id: parseInt(i.vid, 10),
          quantity: i.qty,
          properties: {
            /* _bundle_id is load-bearing: the Shopify Function discounts ONLY lines that
               carry it, and groups them by its value. Renaming it silently kills the
               discount without any error anywhere. */
            _source: 'Bundle Builder',
            _bundle_id: bid,
            _bundle_tier: tierKey,
            _bundle_slot: 'slot' + slot + '-' + (i.cat || 'item')
          }
        });
      });

      /* Any tier carrying a gift product hands it over — not only tiers whose reward TYPE
         is "Free gift". The customizer shows the gift field on every tier, so "15% off AND
         a cooler at $450" is a reasonable rung to build.
         Kept in their own array rather than appended: gifts are stock-limited and set to
         deny overselling, and Shopify rejects an ENTIRE /cart/add if any one line fails.
         A sold-out cooler would therefore stop every bundle over $450 from being bought at
         all, so the add is retried without the gift rather than losing the whole order. */
      var gifts = unlocked.filter(function (t) { return t.giftVariant; }).map(function (t) {
        return {
          id: t.giftVariant,
          quantity: 1,
          properties: { _gift: 'true', _source: 'Bundle Builder', _bundle_id: bid }
        };
      });

      var settled = false;
      function done() {
        if (settled) return;
        settled = true;
        self.busy = false;
        labels.forEach(function (el, ix) { el.textContent = prev[ix]; });
        self.render();
      }
      var watchdog = setTimeout(done, 12000);

      /* fire composition tracking before the network call, so a build is still measured
         if the shopper leaves mid-request */
      this.emit(bid, tierKey, disc, unlocked, n, p);

      /* Judge success on the HTTP status as well as the JSON body. A sold-out line comes
         back as JSON with status 422, but a rate limit or a server error returns an HTML
         page — parsing that yielded {} , which used to read as SUCCESS. The bundle was then
         cleared and the shopper sent to a checkout with nothing in it. */
      /* Every build mints a fresh _bundle_id and adds its own gift. Building twice therefore
         STACKED them: the absorb module moves the earlier bundle's PRODUCTS into the new
         bundle but deliberately skips _gift lines, so the old gift was left behind under a
         bundle id that no longer had any products (order #53315, 30 Sep 2026 — two coolers).
         One builder gift per cart: clear any earlier one before this build adds its own.
         Runs even when this build earns no gift, so dropping below the threshold drops it. */
      function clearBuilderGifts() {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var updates = {};
            (cart.items || []).forEach(function (i) {
              var pr = i.properties || {};
              if (pr._gift && pr._source === 'Bundle Builder') updates[i.key] = 0;
            });
            if (!Object.keys(updates).length) return;
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: updates })
            });
          })
          .catch(function () {});
      }

      function post(payload) {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload })
        }).then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (d) {
            return { ok: r.ok && !(d && d.status && d.status !== 200), data: d };
          });
        });
      }

      clearBuilderGifts()
        .then(function () { return post(items.concat(gifts)); })
        .then(function (res) {
          // gift sold out — keep the sale, drop the gift
          if (!res.ok && gifts.length) return post(items);
          return res;
        })
        .then(function (res) {
          if (!res.ok) throw new Error((res.data && res.data.description) || 'add failed');
          return waitForGifts(self.ctaAction === 'checkout', 3000);
        })
        .then(function () {
          clearTimeout(watchdog);
          self.sel.clear();
          if (self.ctaAction === 'checkout') { window.location.href = '/checkout'; return; }
          /* repaint so the drawer shows the discount the function has applied */
          return refreshDrawer().then(function () { done(); openCartDrawer(); });
        })
        .catch(function () {
          clearTimeout(watchdog);
          done();
          openCartDrawer();
          refreshDrawer();
        });
    }

    /* Composition tracking, including builds that never convert — the more interesting
       dataset. Rides on whatever analytics the store already has; silent if none. */
    emit(bid, tierKey, disc, unlocked, n, p) {
      var handles = [], cats = [], value = 0;
      this.sel.forEach(function (i) {
        for (var k = 0; k < i.qty; k++) handles.push(i.handle);
        if (cats.indexOf(i.cat) === -1) cats.push(i.cat);
        value += i.price * i.qty;
      });
      var pct = disc ? disc.pct : 0;
      var saved = this.sale ? this.saleSavings() : Math.round(value * pct / 100);

      var payload = {
        bundle_id: bid,
        tier: tierKey,
        mode: this.sale ? 'sale' : this.mode,
        sale_mode: !!this.sale,
        discount_pct: pct,
        rewards: unlocked.map(function (t) { return t.type; }),
        item_count: n,
        handles: handles,
        categories: cats,
        subtotal: value / 100,
        savings: saved / 100,
        total: (value - (this.sale ? 0 : saved)) / 100
      };

      try {
        document.documentElement.dispatchEvent(new CustomEvent('bundle:built', { bubbles: true, detail: payload }));
      } catch (e) {}
      try {
        if (window.klaviyo && typeof window.klaviyo.push === 'function') {
          window.klaviyo.push(['track', 'Built Bundle', payload]);
        }
      } catch (e) {}
      try {
        if (window.dataLayer && window.dataLayer.push) {
          window.dataLayer.push(Object.assign({ event: 'bundle_built' }, payload));
        }
      } catch (e) {}
    }
  }

  if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);
})();
